import { useModals, Modal, ModalProps, Button, Skeleton, Stack, Title } from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmployeeUpdate } from "@/queries/employees/use-employees.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployee, ApiEmployeeEditPayload } from "@/types/employees/employee.types.ts";
import { ApiAppConfigUIEmployeeProfileSection } from "@/types/config.types.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Fragment, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

export interface EmployeeEditModalProps extends ModalProps {
  employee: ApiEmployee;
}

const employeePayloadFieldByType: Partial<Record<ApiFieldTypes, keyof ApiEmployeeEditPayload>> = {
  [ApiFieldTypes.employee_first_name]: "first_name",
  [ApiFieldTypes.employee_name_pronunciation]: "name_pronunciation",
  [ApiFieldTypes.employee_last_name]: "last_name",
  [ApiFieldTypes.employee_middle_name]: "middle_name",
  [ApiFieldTypes.employee_phone]: "phone",
  [ApiFieldTypes.employee_phone_personal]: "phone_personal",
  [ApiFieldTypes.employee_email]: "email",
  [ApiFieldTypes.employee_email_personal]: "email_personal",
  [ApiFieldTypes.employee_number]: "personnel_number",
  [ApiFieldTypes.employee_address_address_line_1]: "address_1",
  [ApiFieldTypes.employee_address_address_line_2]: "address_2",
  [ApiFieldTypes.employee_address_city]: "city",
  [ApiFieldTypes.employee_address_zip_code]: "zip",
  [ApiFieldTypes.employee_address_state]: "state",
  [ApiFieldTypes.employee_address_country]: "country_id",
  [ApiFieldTypes.employee_date_birth]: "date_birth",
  [ApiFieldTypes.employee_gender]: "gender",
};

export function EmployeeEditModal({ employee, ...props }: EmployeeEditModalProps) {
  const { t } = useTranslation();
  const { data: appConfig, isLoading: appConfigIsLoading } = useAppConfig();
  const { close } = useModals();
  const { id } = { ...props };
  const navigate = useNavigate();
  const { transformFormDataToPayload, getSectionTitle } = useCustomFieldsHook();
  const configuredSections: ApiAppConfigUIEmployeeProfileSection[] = useMemo(() => {
    return (
      appConfig?.ui.employee_profile_edit.tabs.flatMap((tabWrapper) =>
        tabWrapper.items
          .map((item) => item.section)
          .filter(
            (section): section is ApiAppConfigUIEmployeeProfileSection =>
              section !== null && section.fields.length > 0
          )
      ) ?? []
    );
  }, [appConfig]);

  const editFieldTypes = useMemo(
    () => configuredSections.flatMap((section) => section.fields.map((field) => field.type)),
    [configuredSections]
  );
  const {
    getValuesForFormByEmployee,
    employeeCustomFieldCanView,
    employeeCustomFieldCanEdit,
    getSchemaForEmployeeEditForm,
    getEmployeeEditFieldByType,
  } = useEmployeeCustomFieldsHook(editFieldTypes);

  const sections = useMemo(
    () =>
      configuredSections.filter((section) =>
        section.fields.some(
          (field) =>
            employeeCustomFieldCanView(field, employee) ||
            employeeCustomFieldCanEdit(field, employee)
        )
      ),
    [configuredSections, employee]
  );

  const formConfig = useMemo(() => {
    return {
      schema: yup.object(getSchemaForEmployeeEditForm(sections, employee)),
      values: getValuesForFormByEmployee(sections, employee),
    };
  }, [sections, employee]);

  const serverFieldMap = useMemo(() => {
    const result: Record<string, string> = {};

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type === ApiFieldTypes.custom) {
          result[`custom_fields.${field.slug}`] = field.slug;
          return;
        }

        const payloadField = employeePayloadFieldByType[field.type];
        if (payloadField) result[payloadField] = field.slug;
      });
    });

    return result;
  }, [sections]);

  type EmployeeSchema = yup.InferType<typeof formConfig.schema>;

  const formHook = useForm<EmployeeSchema>({
    mode: "onSubmit",
    resolver: yupResolver(formConfig.schema),
    defaultValues: {},
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formHook;

  const employeeSave = useEmployeeUpdate(control, serverFieldMap);

  useEffect(() => {
    if (appConfig && !appConfigIsLoading) {
      reset(formConfig.values);
    }
  }, [appConfig, appConfigIsLoading, formConfig, reset, employee]);

  const onSubmit = handleSubmit(async (employeeData) => {
    const payload: ApiEmployeeEditPayload = transformFormDataToPayload(sections, employeeData);

    try {
      const savedEmployee = await employeeSave.mutateAsync({
        id: employee.id,
        payload: payload,
      });

      if (employee.id == 0) {
        analyticsService.trackEvent(analyticEvents.employees.created, {
          [analyticProperties.id]: savedEmployee.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.employees.edited, {
          [analyticProperties.id]: employee.id,
          [analyticProperties.actionType]: "Personal Details Edited",
        });
      }

      navigate(`/employees/${savedEmployee.id}`);

      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      size={"lg"}
      {...props}
      title={employee.id == 0 ? t("core:new_employee") : t("core:edit_employee")}
      layout={"stretch"}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={employeeSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        {appConfigIsLoading && (
          <Stack gap={"sm"}>
            <Stack className={"pt-6 pb-4"}>
              <Skeleton className={"h-5"} />
            </Stack>
            <Stack className={"pt-6 pb-4"}>
              <Skeleton className={"h-5"} />
            </Stack>
            <Stack className={"pt-6 pb-4"}>
              <Skeleton className={"h-5"} />
            </Stack>
          </Stack>
        )}
        {sections.map((sectionWrapper, index) => (
          <Stack key={`section-${sectionWrapper.section.id}`}>
            {sectionWrapper.section.id != 0 ? (
              <Title
                size={"sm"}
                header={getSectionTitle(sectionWrapper.section)}
                paddingTop={index != 0}
                paddingBottom
              />
            ) : (
              <></>
            )}

            {sectionWrapper.fields.some((field) => employeeCustomFieldCanView(field, employee)) ? (
              <>
                {sectionWrapper.fields.map((field) => (
                  <Fragment key={`field-${field.slug}`}>
                    {employeeCustomFieldCanView(field, employee) ? (
                      <Stack>{getEmployeeEditFieldByType(field, employee, formHook)}</Stack>
                    ) : (
                      <></>
                    )}
                  </Fragment>
                ))}
              </>
            ) : (
              <></>
            )}
          </Stack>
        ))}
      </form>
    </Modal>
  );
}
